import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import SimpleButton from "./SimpleButton";

interface AlertModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  title: string;
  message: string;
  type?: "success" | "error" | "warning" | "info";
  onConfirm?: () => void;
  confirmText?: string;
}

export default function AlertModal({
  show,
  setShow,
  title,
  message,
  type = "info",
  onConfirm,
  confirmText = "OK",
}: AlertModalProps) {
  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircleIcon className="h-12 w-12 text-mint-green" />;
      case "error":
        return <ExclamationTriangleIcon className="h-12 w-12 text-red-500" />;
      case "warning":
        return <ExclamationTriangleIcon className="h-12 w-12 text-orange" />;
      default:
        return <InformationCircleIcon className="h-12 w-12 text-blue-500" />;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case "success":
        return "border-mint-green";
      case "error":
        return "border-red-500";
      case "warning":
        return "border-orange";
      default:
        return "border-blue-500";
    }
  };

  const getButtonColor = () => {
    switch (type) {
      case "success":
        return "green";
      case "error":
        return "red";
      case "warning":
        return "orange";
      default:
        return "blue";
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    setShow(false);
  };

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setShow}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className={`relative transform rounded-2xl bg-white border-4 ${getBorderColor()} shadow-2xl transition-all w-full max-w-md p-6 sm:p-8`}>
                {/* Close Button */}
                <button
                  type="button"
                  onClick={() => setShow(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>

                {/* Content */}
                <div className="text-center">
                  {/* Icon */}
                  <div className="mx-auto flex items-center justify-center mb-4">
                    {getIcon()}
                  </div>

                  {/* Title */}
                  <Dialog.Title as="h3" className="text-xl sm:text-2xl font-bold text-dark-grey mb-3">
                    {title}
                  </Dialog.Title>

                  {/* Message */}
                  <p className="text-sm sm:text-base text-dark-grey mb-6">
                    {message}
                  </p>

                  {/* Button */}
                  <div className="flex justify-center">
                    <SimpleButton
                      text={confirmText}
                      colour={getButtonColor()}
                      onClick={handleConfirm}
                      className="min-w-[120px]"
                    />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
